import { useEffect } from "react";
import type { MemeItem } from "@/mocks/homeData";

interface LightboxProps {
  item: MemeItem | null;
  onClose: () => void;
  aspectClass?: string;
}

export default function Lightbox({ item, onClose, aspectClass = "aspect-square" }: LightboxProps) {
  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-foreground-950/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="关闭"
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-primary-500 transition-colors cursor-pointer"
      >
        <i className="ri-close-line text-2xl" />
      </button>
      <div
        className="rise-in w-full max-w-2xl overflow-hidden rounded-3xl bg-background-50 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <img
            src={item.img}
            alt={item.alt}
            className={`${aspectClass} w-full object-cover`}
          />
          <span className="absolute left-3 top-3 rounded-full bg-secondary-500 px-3 py-1 text-xs font-semibold text-foreground-950">
            {item.tag}
          </span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5">
          <div className="min-w-0">
            <h4 className="font-heading text-lg text-foreground-950">{item.title}</h4>
            <p className="mt-1 truncate text-sm text-foreground-600">{item.caption}</p>
          </div>
          <a
            href={item.img}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-download-2-line" />
            打开原图
          </a>
        </div>
      </div>
    </div>
  );
}